import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, BookOpen, Heart, Map, School, Camera } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const HighlandLocalPage = () => {
  const initiatives = [
    {
      icon: <Map className="w-6 h-6" />,
      title: "Local Research Networks",
      description: "Long-term collaborative research with local communities, indigenous groups, and grassroots organizations",
      link: "https://highlandinstitute.org/category/highland-local/"
    },
    {
      icon: <School className="w-6 h-6" />,
      title: "Schools & Citizen Science",
      description: "Partnerships with schools and community-led biodiversity monitoring and environmental research",
      links: [
        { label: "Schools Twinning Projects", url: "https://highlandinstitute.org/schools-twinning-projects/" },
        { label: "Citizen Science & Biodiversity Work", url: "https://highlandinstitute.org/citizen-science-and-biodiversity-work/" }
      ]
    },
    {
      icon: <BookOpen className="w-6 h-6" />,
      title: "Community Archives & Oral Histories",
      description: "Collaborative documentation of local knowledge, oral traditions, and cultural heritage preservation",
      link: "https://highlandinstitute.org/archive-oral-history-projects/"
    },
    {
      icon: <Camera className="w-6 h-6" />,
      title: "Public Exhibitions & Events",
      description: "Community-centered exhibitions, cultural events, and public engagement programmes",
      link: "https://highlandinstitute.org/category/events-2/"
    }
  ];

  const projects = [
    {
      title: "Biodiversity Monitoring",
      description: "Working with local communities to document and monitor biodiversity across Highland Asia, combining indigenous ecological knowledge with scientific methods.",
      image: "https://customer-assets.emergentagent.com/job_institute-clone/artifacts/2exxpl15_Landscape%20Fieldwork%20Earthkeepers.jpg"
    },
    {
      title: "Oral History Projects",
      description: "Documenting local histories, traditional knowledge, and intangible cultural heritage through collaborative oral history initiatives with community elders.",
      image: "https://customer-assets.emergentagent.com/job_institute-clone/artifacts/icn000wy_Community%20Knowledge.jpg"
    },
    {
      title: "School Partnerships",
      description: "Engaging with local schools to promote environmental awareness, research skills, and connection to place through hands-on learning experiences.",
      image: "https://customer-assets.emergentagent.com/job_institute-clone/artifacts/owu7mbhm_61845373_1098886370314453_8241980646898008064_n.jpg"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-green-900 to-green-700 text-white py-24">
        <div className="absolute inset-0 bg-black/40"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_institute-clone/artifacts/rsn0xi45_Highland%20Local%20Community%20Knowledge%20Games.jpg)'
          }}
        ></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Highland Local
            </h1>
            <p className="text-2xl text-green-100 leading-relaxed mb-8">
              Rooted Collaborations, Community Knowledge
            </p>
            <p className="text-xl text-white max-w-3xl mx-auto">
              From long-term field research to local school partnerships, public exhibitions, and citizen science initiatives, 
              here are everyday engagements that shape our research and learning environments.
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">Community-Centered Research</h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-6">
                The Highland Institute is deeply rooted in the communities of Highland Asia. Our local engagement goes beyond 
                traditional academic research—we collaborate with communities as equal partners in knowledge production, 
                cultural preservation, and sustainable development.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                Through long-term relationships built on trust and mutual respect, we work with local communities, schools, 
                grassroots organizations, and indigenous groups to co-create research that serves community needs and priorities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Initiatives */}
      <section className="py-16 bg-gray-50" id="research">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Our Local Initiatives</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {initiatives.map((initiative, index) => (
              <Card key={index} className="hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4 text-green-600">
                    {initiative.icon}
                  </div>
                  <CardTitle className="text-xl">{initiative.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">{initiative.description}</p>
                  {initiative.link && (
                    <a 
                      href={initiative.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-green-600 hover:text-green-700 font-medium"
                    >
                      Learn More
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </a>
                  )}
                  {initiative.links && (
                    <div className="space-y-2">
                      {initiative.links.map((link, idx) => (
                        <a 
                          key={idx}
                          href={link.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center text-sm text-green-600 hover:text-green-700 font-medium"
                        >
                          <ArrowRight className="mr-2 w-3 h-3" />
                          {link.label}
                        </a>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-16 bg-white" id="schools">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Featured Local Projects</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {projects.map((project, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 overflow-hidden">
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <h3 className="text-2xl font-bold mb-2">{project.title}</h3>
                  </div>
                </div>
                <CardContent className="pt-6">
                  <p className="text-gray-600">{project.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Community Archives */}
      <section className="py-16 bg-gray-50" id="archives">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">Community Archives & Oral Histories</h2>
            <div className="bg-white p-8 rounded-lg shadow-md">
              <p className="text-gray-700 leading-relaxed mb-6">
                Our oral history and archival projects document the rich cultural heritage and local knowledge of Highland 
                communities. These collaborative initiatives ensure that community voices, stories, and traditional knowledge 
                are preserved for future generations.
              </p>
              <div className="border-l-4 border-green-600 pl-6 mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">What We Document</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 text-green-600 mt-1 mr-2 flex-shrink-0" />
                    <span className="text-gray-700">Traditional ecological knowledge and resource management practices</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 text-green-600 mt-1 mr-2 flex-shrink-0" />
                    <span className="text-gray-700">Oral histories, folklore, and intangible cultural heritage</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 text-green-600 mt-1 mr-2 flex-shrink-0" />
                    <span className="text-gray-700">Community responses to environmental and social change</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 text-green-600 mt-1 mr-2 flex-shrink-0" />
                    <span className="text-gray-700">Local languages, songs, and cultural practices</span>
                  </li>
                </ul>
              </div>
              <a 
                href="https://highlandinstitute.org/archive-oral-history-projects/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center text-green-600 hover:text-green-700 font-medium"
              >
                Explore Archive Projects
                <ArrowRight className="ml-2 w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Public Events */}
      <section className="py-16 bg-white" id="events">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Public Exhibitions & Events</h2>
            <p className="text-gray-700 leading-relaxed mb-8">
              We regularly organize public exhibitions, cultural events, and community gatherings that bring research to life 
              and create spaces for dialogue, learning, and celebration of local knowledge and culture.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/events">
                <Button className="bg-green-600 hover:bg-green-700 px-8 py-6 text-lg">
                  View Events
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="https://highlandinstitute.org/category/events-2/" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-green-600 text-green-600 hover:bg-green-50 px-8 py-6 text-lg">
                  Past Events
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-green-600 to-green-700 text-white" id="engage">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Engage Locally</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Interested in partnering with us or learning more about our local initiatives? Get in touch.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact">
              <Button className="bg-white text-green-600 hover:bg-gray-100 px-8 py-6 text-lg">
                Contact Us
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/community">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                Community Page
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HighlandLocalPage;
